/**
 * Structured application logger.
 *
 * Outputs ONLY in development mode (`import.meta.env.DEV === true`).
 * In production builds: all logs are silenced — zero data leakage.
 *
 * Rules:
 * - Never log full API payloads (use counts or IDs instead)
 * - Never log sensitive fields (passwords, tokens, financial amounts)
 * - Always import this instead of calling console.* directly
 *
 * @example
 * import { logger } from '@/core/logger';
 *
 * logger.info('Fetched societies', { count: data.length });
 * logger.error('API call failed', error.message);
 * logger.warn('Retrying request', { attempt: 2, url: '/societies' });
 * logger.debug('Payload shape', { keys: Object.keys(payload) });
 */

const IS_DEV = typeof import.meta !== 'undefined' && Boolean(import.meta.env?.DEV);

type LogContext = Record<string, unknown> | string | number | undefined;

function emit(
  level: 'info' | 'warn' | 'error',
  message: string,
  context?: LogContext,
): void {
  if (!IS_DEV) return;
  const tag = `[XS]`;
  context !== undefined
    ? console[level](tag, message, context)
    : console[level](tag, message);
}

export const logger = {
  /** General informational log — use for successful operations */
  info: (message: string, context?: LogContext) => emit('info', message, context),

  /** Warning log — use for non-fatal unexpected states */
  warn: (message: string, context?: LogContext) => emit('warn', message, context),

  /** Error log — use for caught exceptions and API failures */
  error: (message: string, context?: LogContext) => emit('error', message, context),

  /** Debug log — alias for info, use for verbose traces during development */
  debug: (message: string, context?: LogContext) => emit('info', `[debug] ${message}`, context),
} as const;
